/**
 * Mantenimientos por vencer — la lista de llamadas del taller y de
 * decisiones del gerente.
 *
 * Solo garantías vigentes. "Le toca" y "Omitido sin decidir" salen siempre
 * (son trabajo pendiente hoy); "Próximo" se filtra por el rango de fechas.
 */
import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import UpcomingOutlinedIcon from "@mui/icons-material/UpcomingOutlined";
import { FP, StatCard, StatusPill, FpDataGrid, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import {
  calendario,
  estadoGarantia,
  fmtFecha,
  sumarDias,
  aFecha,
  ESTADOS_SERVICIO,
} from "./calendario";
import { sectionBox, fmtPrecio } from "./comunes";
import RegistrarServicio from "./registrarservicio";

export default function ReporteMantenimientos({ config, hoy, onVer, toast }) {
  const { post } = useApi();
  const permisos = config.permisos || {};
  const sucursales = config.sucursales || [];
  // Sin alcance multi-sucursal el backend fuerza la del usuario: el
  // selector queda fijo en ella.
  const [sucursal, setSucursal] = useState(config.alcanceTotal ? "" : config.sucursalUsuario || "");
  const [datos, setDatos] = useState({ garantias: [], servicios: [] });
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const { garantias, servicios } = datos;
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(sumarDias(hoy, 30));
  const [estado, setEstado] = useState("");
  const [registrar, setRegistrar] = useState(null); // garantía

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/garantias_reporte", { sucursal });
      if (ok) setDatos({ garantias: data.garantias || [], servicios: data.servicios || [] });
      else toast.error(data?.Error || "No se pudieron leer los mantenimientos");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [sucursal]); // eslint-disable-line react-hooks/exhaustive-deps

  const registrarServicio = async (servicio) => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/garantias_servicio", {
        ...servicio,
        garantia: servicio.garantiaId,
      });
      if (!ok) {
        toast.error(data?.Error || "No se pudo registrar el servicio");
        return;
      }
      toast.exito("Servicio registrado");
      setRegistrar(null);
      await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const filas = useMemo(() => {
    const out = [];
    garantias
      .filter((g) => estadoGarantia(g, hoy) === "vigente")
      .forEach((g) => {
        calendario(g, servicios, hoy).forEach((s) => {
          const pendiente = s.estado === "letoca" || (s.estado === "omitido" && !s.decision);
          const enRango =
            s.estado === "proximo" &&
            aFecha(s.fecha) >= aFecha(desde) &&
            aFecha(s.fecha) <= aFecha(hasta);
          if (!pendiente && !enRango) return;
          out.push({
            id: `${g.id}-${s.numero}`,
            garantia: g,
            cliente: g.cliente,
            telefono: g.telefono,
            moto: `${g.marca} ${g.modelo}`,
            chasis: g.chasis,
            sucursal: g.sucursalNombre,
            servicio: `${s.numero} · ${fmtPrecio(s.precio)}`,
            fecha: s.fecha,
            hasta: s.hasta,
            estado: s.estado,
          });
        });
      });
    const orden = { omitido: 0, letoca: 1, proximo: 2 };
    return out.sort(
      (a, b) => orden[a.estado] - orden[b.estado] || aFecha(a.fecha) - aFecha(b.fecha)
    );
  }, [garantias, servicios, hoy, desde, hasta]);

  const visibles = estado ? filas.filter((f) => f.estado === estado) : filas;
  const cuenta = (e) => filas.filter((f) => f.estado === e).length;

  const columnas = [
    { field: "cliente", headerName: "Cliente", flex: 1.2, minWidth: 150 },
    { field: "telefono", headerName: "Teléfono", width: 120 },
    { field: "moto", headerName: "Moto", flex: 0.9, minWidth: 120 },
    { field: "sucursal", headerName: "Sucursal", width: 105 },
    { field: "servicio", headerName: "Servicio", width: 115 },
    { field: "fecha", headerName: "Fecha", width: 105, valueFormatter: (v) => fmtFecha(v) },
    { field: "hasta", headerName: "Ventana hasta", width: 135, valueFormatter: (v) => fmtFecha(v) },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      renderCell: ({ value }) => (
        <StatusPill
          label={ESTADOS_SERVICIO[value].label}
          variant={ESTADOS_SERVICIO[value].variant}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 96,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          {permisos.registrar ? (
            <Tooltip title="Registrar servicio">
              <IconButton size="small" onClick={() => setRegistrar(row.garantia)}>
                <BuildOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Ver garantía">
            <IconButton size="small" onClick={() => onVer(row.chasis)}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const calRegistrar = registrar ? calendario(registrar, servicios, hoy) : [];

  return (
    <Stack spacing={2.5}>
      <Box sx={sectionBox}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              label="Sucursal"
              value={sucursal}
              onChange={(e) => setSucursal(e.target.value)}
              disabled={!config.alcanceTotal}
              sx={fpInputSx}
            >
              {config.alcanceTotal ? <MenuItem value="">Todas</MenuItem> : null}
              {sucursales.map((s) => (
                <MenuItem key={s.codigo} value={s.codigo}>
                  {s.nombre || s.codigo}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Próximos desde"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Hasta"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              label="Estado"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              sx={fpInputSx}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="letoca">Le toca</MenuItem>
              <MenuItem value="omitido">Omitidos sin decidir</MenuItem>
              <MenuItem value="proximo">Próximos</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Le toca ahora"
            value={cuenta("letoca")}
            icon={EventAvailableOutlinedIcon}
            accent={FP.color.info}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Omitidos sin decidir"
            value={cuenta("omitido")}
            icon={WarningAmberRoundedIcon}
            accent={FP.color.danger}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Próximos en el rango"
            value={cuenta("proximo")}
            icon={UpcomingOutlinedIcon}
          />
        </Grid>
      </Grid>

      <Box sx={{ ...sectionBox, p: 0, overflow: "hidden" }}>
        <Box sx={{ height: 480 }}>
          <FpDataGrid
            rows={visibles}
            columns={columnas}
            loading={cargando}
            vacioTitulo="Sin mantenimientos pendientes"
            vacioSubtitulo="Nadie tiene servicio en ventana ni omitido en esta selección."
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </Box>

      <RegistrarServicio
        abierto={!!registrar}
        garantia={registrar}
        calendario={calRegistrar}
        hoy={hoy}
        guardando={guardando}
        onCerrar={() => setRegistrar(null)}
        onRegistrar={registrarServicio}
      />
    </Stack>
  );
}
